import {
  AdornmentBox,
  Checkbox,
  CheckboxField,
  Collapse,
  ConfirmationDialog,
  FormControlLabel,
  FormikCheckbox,
  FormikDropdownSelect,
  FormikHtmlEditor,
  FormikInputField,
  FormSection,
  FormSectionTitle,
  Grid,
  InputLabel,
  LearnMoreLink,
  Tooltip,
  Typography,
  SwitchField,
  useClientOptions,
  useDialogs,
  useUser
} from '@paintscout/ui'
import { getEmailTemplates, getFeature, getObjectLabels } from '@paintscout/util/builder'
import { getIndefiniteArticle, getTemplateContext, getTemplateTags, makeHtml } from '@paintscout/util/util'
import { FastField, Field, useFormikContext } from 'formik'
import type { InternalOptionsDocument, OptionsDocument } from 'paintscout'
import React from 'react'
import WarningIcon from '@material-ui/icons/Warning'
import { parseNumber } from '@paintscout/util/calculator'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'

const useStyles = makeStyles((theme: Theme) => ({
  sectionSpacing: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  badgeRoot: {
    flexShrink: 'unset',
    marginRight: 2
  }
}))

function PaymentOptions({ provider }: { provider: 'stripe' | 'stripeW1d' | 'payrix' }) {
  const classes = useStyles()
  const { values, errors, setFieldValue } = useFormikContext<{
    options: OptionsDocument
    internalOptions?: InternalOptionsDocument
  }>()
  const { options } = values
  const isStripe = provider === 'stripe' || provider === 'stripeW1d'
  const integrationKey = provider === 'stripeW1d' ? 'stripeW1d' : 'stripe'
  const integrationPath = `internalOptions.options.integrations.${integrationKey}`
  const isCanadianPayrix = options.options.integrations?.payrix?.config?.country === 'Canada' && provider === 'payrix'
  const achLabel = isCanadianPayrix ? 'EFT' : 'ACH'

  const hasSettingsCommunications = getFeature({ options, path: 'settingsCommunications.enabled' })
  const { preferences } = useUser()
  const { isAdmin } = useClientOptions()
  const { openDialog, dismissDialog } = useDialogs()
  const { quote: quotesLabel, currency } = getObjectLabels({ options, invoice: false })
  const invoicesLabel = getObjectLabels({ options, invoice: true }).quote
  const quoteSingular = quotesLabel.value.toLowerCase()
  const quotePlural = quotesLabel.plural.toLowerCase()
  const invoiceSingular = invoicesLabel.value.toLowerCase()
  const invoicePlural = invoicesLabel.plural.toLowerCase()

  const emailTemplates = getEmailTemplates({ options })
  const emailTemplate = {
    key: 'payment-received',
    ...emailTemplates['payment-received']
  }

  options.options.emails.values[emailTemplate.name] = options.options.emails.values[emailTemplate.name] ?? emailTemplate

  const editorPreference = preferences?.editor ?? 'always'
  const templateContext = getTemplateContext(editorPreference)
  const templateTags = getTemplateTags({ options, invoice: true })

  const internationalMode = options.options.internationalMode

  return (
    <>
      {isAdmin && provider === 'payrix' && (
        <>
          <FormSection hideDivider classes={{ root: classes.sectionSpacing }}>
            <FormSectionTitle title={'Payrix Merchant Id'} variant={'h4'} />
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="body1">{options.options.integrations?.payrix?.apiKey || 'Not Found'}</Typography>
              </Grid>
            </Grid>
          </FormSection>
        </>
      )}
      <FormSection hideDivider classes={{ root: classes.sectionSpacing }}>
        <FormSectionTitle title={quotesLabel.plural} variant={'h4'} />
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <CheckboxField
              name="options.options.quotes.allowPaymentsOnQuotes"
              label={
                <Tooltip
                  classes={{ badgeRoot: classes.badgeRoot }}
                  content={`If this box is enabled, customers will be prompted to pay a deposit after accepting their ${quoteSingular}.`}
                >
                  <>{`Collect a deposit payment when ${quotePlural} are accepted.`}</>
                </Tooltip>
              }
            />
          </Grid>

          <Grid item xs={12} sm={5} md={4} alignItems="flex-end">
            <Field
              component={FormikInputField}
              label="Default Deposit Amount"
              name="options.options.quotes.depositAmount.value"
              fullWidth
              format="percent"
              error={errors.options?.options?.quotes?.depositAmount}
              disabled={!values.options.options.quotes.allowPaymentsOnQuotes}
              noAdornmentMargin
              endAdornment={
                <AdornmentBox>
                  <Field
                    component={FormikDropdownSelect}
                    name="options.options.quotes.depositAmount.type"
                    fullWidth
                    placeholder="%"
                    nestedDropdown
                    options={[
                      { label: '%', value: 'percentage' },
                      { label: currency.symbol, value: 'amount' }
                    ]}
                    disabled={!values.options.options.quotes.allowPaymentsOnQuotes}
                  />
                </AdornmentBox>
              }
            />
          </Grid>
          <Grid item xs={12} />
          {provider === 'payrix' && (
            <Grid item xs={12} sm={5} md={4} alignItems="flex-end">
              <InputLabel
                disabled={!values.options.options.quotes.allowPaymentsOnQuotes}
                style={{ marginBottom: '5px' }}
              >
                Allow Deposit Payments By
              </InputLabel>
              <Field
                component={FormikDropdownSelect}
                fullWidth
                disabled={!values.options.options.quotes.allowPaymentsOnQuotes}
                name="options.options.quotes.allowedDepositTypes"
                placeholder="Credit Card"
                options={[
                  { label: 'Credit Card', value: 'credit' },
                  { label: achLabel, value: 'ach' },
                  { label: `Credit Card or ${achLabel}`, value: 'both' }
                ].filter(Boolean)}
              />
            </Grid>
          )}

          {isAdmin && (
            <Grid item xs={12}>
              <Collapse show={values?.options?.options?.quotes?.depositAmount?.type !== 'amount'}>
                <CheckboxField
                  name="options.options.quotes.depositAmountBeforeTax"
                  label={'Use balance due before tax when calculating the deposit amount'}
                  sublabel={'By default, the deposit amount is a percentage of the balance due after tax'}
                  disabled={!values.options.options.quotes.allowPaymentsOnQuotes}
                />
              </Collapse>
            </Grid>
          )}
        </Grid>
      </FormSection>
      <FormSection classes={{ root: classes.sectionSpacing }}>
        <FormSectionTitle title={invoicesLabel.plural} variant={'h4'} />
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <CheckboxField
              name="options.options.quotes.allowPaymentsOnInvoices"
              label={`Allow credit card payments on ${invoicePlural}.`}
            />
          </Grid>
          {provider === 'payrix' && (
            <Grid item xs={12}>
              <CheckboxField
                name="options.options.quotes.allowACHPaymentsOnInvoices"
                label={`Allow ${achLabel} payments on ${invoicePlural}.`}
              />
            </Grid>
          )}
        </Grid>
      </FormSection>

      {!isCanadianPayrix && (
        <FormSection classes={{ root: classes.sectionSpacing }}>
          <FormSectionTitle
            title="Surcharging"
            variant="h4"
            subTitle={
              'Pass on payment processing fees to your clients. Please note: it may not be legal in some areas — review local laws before enabling.'
            }
          />
          <Grid container spacing={1}>
            <Grid item xs={12} style={{ margin: '8px 18px 0 0' }}>
              <SwitchField
                name="options.options.quotes.surcharge.enabled"
                label={'Enable surcharging on payments made by credit card.'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const enabling = e.target.checked
                  openDialog(ConfirmationDialog, {
                    yesLabel: enabling ? 'Yes, Enable' : 'Yes, Disable',
                    message: (
                      <>
                        <p>
                          {enabling ? 'Surcharging will only apply to' : 'Surcharging will be turned off for'}{' '}
                          <span style={{ fontWeight: 700 }}>
                            new {quotePlural} and {invoicePlural}
                          </span>{' '}
                          created from this point forward.
                        </p>
                        <p>
                          However, if you create a new {invoiceSingular} from {getIndefiniteArticle(quoteSingular)}{' '}
                          {quoteSingular} that already exists, that {invoiceSingular} will continue to use the previous
                          surcharge settings from the {quoteSingular}.
                        </p>
                      </>
                    ),
                    onConfirm: () => {
                      dismissDialog()
                      setFieldValue('options.options.quotes.surcharge.enabled', enabling)
                    },
                    onCancel: () => {
                      dismissDialog()
                    }
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} container>
              <div style={{ width: '100%' }}>
                <Collapse show={values?.options?.options?.quotes?.surcharge?.enabled}>
                  <Grid item xs={12} sm={5} md={4} style={{ marginTop: 12, marginBottom: 12 }}>
                    <Field
                      component={FormikInputField}
                      label="Surcharge Amount (%)"
                      name="options.options.quotes.surcharge.value"
                      fullWidth
                      type="number"
                      disabled={!values?.options?.options?.quotes?.surcharge?.enabled}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!options?.options?.quotes?.surcharge?.applyToDeposit}
                          onChange={(e) => {
                            setFieldValue('options.options.quotes.surcharge.applyToDeposit', !e.target.checked)
                          }}
                          disabled={!values?.options?.options?.quotes?.surcharge?.enabled}
                        />
                      }
                      label={`Don't include surcharge on deposit payments.`}
                    />
                  </Grid>

                  {provider === 'payrix' && (
                    <Grid item xs={12}>
                      <CheckboxField
                        name="options.options.quotes.surcharge.processDebit"
                        label={
                          <Tooltip
                            classes={{ badgeRoot: classes.badgeRoot }}
                            content={`Debit card payments can't be surcharged due to legal restrictions. If this box is unchecked, customers will not be able to pay with debit while surcharging is enabled.`}
                          >
                            <>{`Allow debit card payments (surcharge won't apply to debit).`}</>
                          </Tooltip>
                        }
                      />
                    </Grid>
                  )}
                </Collapse>
              </div>
            </Grid>
          </Grid>
        </FormSection>
      )}

      {!hasSettingsCommunications && (
        <FormSection classes={{ root: classes.sectionSpacing }}>
          <Grid container item spacing={1}>
            <Grid item xs={12}>
              <FormSectionTitle
                title={'Payment Received Email'}
                variant={'h4'}
                subTitle={`This email is sent whenever a payment is received or manually added.`}
              />
            </Grid>
            <Grid item xs={12}>
              <FastField
                label={'Subject'}
                content={makeHtml(emailTemplate.subject, { replaceBreaks: true })}
                component={FormikHtmlEditor}
                debounce={true}
                name={`options.options.emails.values.${emailTemplate.name}.subject`}
                templateContext={templateContext}
                toolbar={{
                  template: {
                    tags: templateTags
                  }
                }}
                singleLine
              />
            </Grid>
            <Grid item xs={12}>
              <FastField
                label={'Body'}
                component={FormikHtmlEditor}
                debounce={true}
                content={makeHtml(emailTemplate.body, { replaceBreaks: true })}
                name={`options.options.emails.values.${emailTemplate.name}.body`}
                templateContext={templateContext}
                toolbar={{
                  template: {
                    tags: templateTags
                  }
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
      )}
      {isStripe && (
        <FormSection classes={{ root: classes.sectionSpacing }}>
          <FormSectionTitle title="Currency" subTitle={`Set your ${provider} payment currency.`} variant="h4" />
          <Grid container>
            <Grid item xs={12} sm={6}>
              <Field
                component={FormikDropdownSelect}
                fullWidth
                name="options.options.currency"
                placeholder="USD"
                options={[
                  { label: 'USD', value: 'USD' },
                  { label: 'CAD', value: 'CAD' },
                  internationalMode && { label: 'GBP', value: 'GBP' },
                  internationalMode && { label: 'EUR', value: 'EUR' },
                  internationalMode && { label: 'AUD', value: 'AUD' }
                ].filter(Boolean)}
              />
            </Grid>
          </Grid>
        </FormSection>
      )}
      {!isAdmin && provider === 'payrix' && (
        <FormSection classes={{ root: classes.sectionSpacing }}>
          <FormSectionTitle title="Terms" variant="h4" />
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="body1">
                Additional payment terms will be added to your existing terms during customer checkout.
              </Typography>
              <LearnMoreLink
                style={{ marginTop: '1em' }}
                link="https://help.paintscout.com/en/articles/11003124-additional-payment-terms"
              />
            </Grid>
          </Grid>
        </FormSection>
      )}
      {/* This should only ever be present from Admin as it isnt tied into saveOptions from App */}
      {isAdmin && isStripe && (
        <FormSection classes={{ root: classes.sectionSpacing }}>
          <FormSectionTitle
            title={`Advanced`}
            subTitle={`Advanced payment settings, careful changing these values!`}
            variant={'h3'}
          />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Tooltip
                icon={<WarningIcon fontSize="small" style={{ color: '#F5A623' }} />}
                interactive
                title="Be careful!"
                content={
                  <ul>
                    <li>
                      This rate will be <strong>added</strong> to the client's base Stripe rate.
                    </li>
                    <li>
                      <strong>Don't input a percentage!</strong> a value of 0.005 means a rate of 0.5%.
                    </li>
                  </ul>
                }
              >
                <Field
                  label="Use a custom Stripe fee rate"
                  name={`${integrationPath}.useCustomFeeRate`}
                  component={FormikCheckbox}
                />
              </Tooltip>
              <Collapse show={!!values.internalOptions?.options?.integrations?.[integrationKey]?.useCustomFeeRate}>
                <Field
                  component={FormikInputField}
                  label="Stripe Fee Rate"
                  name={`${integrationPath}.feeRate`}
                  onChange={(e) => {
                    setFieldValue(
                      `${integrationPath}.feeRate`,
                      Math.min(Math.max(parseNumber(e.target.value), 0), 0.01)
                    )
                  }}
                  type="number"
                  placeholder={0}
                  default={0}
                  inputProps={{ min: 0, max: 0.01, step: 0.001 }}
                  error={errors?.internalOptions?.options?.integrations?.[integrationKey]?.feeRate}
                />
              </Collapse>
            </Grid>
          </Grid>
        </FormSection>
      )}
    </>
  )
}

export default PaymentOptions
